import {
  AuthRestApiResultSchemaModel,
  EProjectAuthType,
  GenericRestApiResquestModel,
  ICommonSettings,
  IGenericAuthSetting,
  ILDAPSetting,
  IOAuth2RestApiResquestModel,
  IOAuth2Setting,
  IStripeSetting
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import * as React from "react";
import { ReactNode } from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { AppConfigAuthGeneric } from "../app-settings-config-auth/app-settings-config-auth-generic";
import { AppConfigAuthLdap } from "../app-settings-config-auth/app-settings-config-auth-ldap";
import { AppConfigAuthOAuth2 } from "../app-settings-config-auth/app-settings-config-auth-oauth2";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const FORM_AUTH_SETTINGS_SEARCH_TERMS: string[] = ["Type"];

interface IPropertiesObjectModel {
  id: number;
  name: string;
  key: string;
  value: string;
  type: string;
}

export interface IConfigFormAuthSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingFormAuth?: IStripeSetting;
  type: EProjectAuthType;
  tokenParameters: Array<IPropertiesObjectModel>;
  refreshTokenParameters: Array<IPropertiesObjectModel>;
  activeKey?: string;
}

export interface IConfigFormAuthSettingsProps {
  isRunConfig?: boolean;
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFormAuthId?: string;
  formAuthSettings: any[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFormAuthSettings extends React.PureComponent<
  IConfigFormAuthSettingsProps,
  IConfigFormAuthSettingsState
> {
  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFormAuthSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingFormAuth: undefined,
      type: 0,
      tokenParameters: [{ id: 1, name: "custom", key: "", value: "", type: "header" }],
      refreshTokenParameters: [{ id: 1, name: "custom", key: "", value: "", type: "header" }]
    };
  }

  componentDidMount() {
    this.loadFormAuthSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFormAuthSettingsProps & IWithSDProps) {
    if (prevProps.editingFormAuthId && !this.props.editingFormAuthId) {
      this.formRef.current?.resetFields();
      this.setState({
        tokenParameters: [{ id: 1, name: "custom", key: "", value: "", type: "header" }],
        refreshTokenParameters: [{ id: 1, name: "custom", key: "", value: "", type: "header" }]
      });
    }
    if (prevProps.editingFormAuthId !== this.props.editingFormAuthId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadFormAuthSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadFormAuthSetting();
    }
  }

  loadFormAuthSetting = () => {
    if (this.props.editingFormAuthId) {
      const formAuth = this.props.formAuthSettings.find(
        (x) => x.id === this.props.editingFormAuthId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingFormAuth: formAuth }, () => {
        if (formAuth) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          if (formAuth.type === EProjectAuthType.genericAuth) {
            this.setState({ type: 1 });
            this.loadTokenParameters(formAuth.tokenRequest);
            this.loadRefreshTokenParameters(formAuth.refreshTokenRequest);
          } else if (formAuth.type === EProjectAuthType.oAuth2) {
            this.setState({ type: 2 });
          } else if (formAuth.type === EProjectAuthType.ldap) {
            this.setState({ type: 3 });
          } else {
            this.setState({ type: 0 });
          }

          if (formAuth.type === EProjectAuthType.genericAuth) {
            fieldsValue.genericAuthName = formAuth.name;
            fieldsValue.tokenUrl = formAuth.tokenRequest.url;
            fieldsValue.tokenExpirationInMinutes = formAuth.tokenRequest.tokenExpirationInMinutes;
            fieldsValue.tokenContentType = formAuth.tokenRequest.contentType;
            fieldsValue.refreshTokenUrl = formAuth.refreshTokenRequest.url;
            fieldsValue.refreshTokenContentType = formAuth.refreshTokenRequest.contentType;
            fieldsValue.accessTokenKeyName = formAuth.resultSchema.accessTokenKeyName;
            fieldsValue.userNameKeyName = formAuth.resultSchema.userNameKeyName;
            fieldsValue.firstNameKeyName = formAuth.resultSchema.firstNameKeyName;
            fieldsValue.lastNameKeyName = formAuth.resultSchema.lastNameKeyName;
            fieldsValue.refreshTokenKeyName = formAuth.resultSchema.refreshTokenKeyName;
          } else if (formAuth.type === EProjectAuthType.oAuth2) {
            fieldsValue.oAuth2Name = formAuth.name;
            fieldsValue.accessTokenKeyName = formAuth.resultSchema.accessTokenKeyName;
            fieldsValue.userNameKeyName = formAuth.resultSchema.userNameKeyName;
            fieldsValue.firstNameKeyName = formAuth.resultSchema.firstNameKeyName;
            fieldsValue.lastNameKeyName = formAuth.resultSchema.lastNameKeyName;
            fieldsValue.refreshTokenKeyName = formAuth.resultSchema.refreshTokenKeyName;
            fieldsValue.accessTokenUrl = formAuth.request.accessTokenUrl;
            fieldsValue.accessTokenExpirationInMinutes = formAuth.request.accessTokenExpirationInMinutes;
            fieldsValue.clientId = formAuth.request.clientId;
            fieldsValue.clientSecret = formAuth.request.clientSecret;
            fieldsValue.clientAuthSendAsBasicAuthHeader =
              formAuth.request.clientAuthSendAsBasicAuthHeader === true ? "true" : "false";
            fieldsValue.scope = formAuth.request.scope;
          } else if (formAuth.type === EProjectAuthType.ldap) {
            fieldsValue.ldapName = formAuth.name;
            fieldsValue.ipAddress = formAuth.ipAddress;
            fieldsValue.userName = formAuth.userName;
            fieldsValue.password = formAuth.password;
            fieldsValue.port = formAuth.port;
            fieldsValue.dn = formAuth.dn;
            fieldsValue.searchDn = formAuth.searchDn;
            fieldsValue.searchTemplate = formAuth.searchTemplate;
            fieldsValue.bindType = formAuth.bindType;
            fieldsValue.protocol = formAuth.protocol;
            fieldsValue.domainPrefixForUserName = formAuth.domainPrefixForUserName;
            fieldsValue.userNameAttributeName = formAuth.userNameAttributeName;
            fieldsValue.firstNameAttributeName = formAuth.firstNameAttributeName;
            fieldsValue.lastNameAttributeName = formAuth.lastNameAttributeName;
            fieldsValue.phoneNumberAttributeName = formAuth.phoneNumberAttributeName;
          }
          fieldsValue.type = formAuth.type;
          this.formRef.current?.setFieldsValue(fieldsValue);
        }
      });
      return;
    }

    this.setState({ type: 0 }, () => {
      this.formRef.current?.resetFields();
    });
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    const nameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isNameValid = nameRules.test(data.formAuthName);
    if (!isNameValid) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Authentication Provider setting name is invalid.");
      return;
    }
    const isSettingAlreadyExist = this.props?.formAuthSettings?.some(
      (x) => x.name === data.formAuthName && !this.props.editingFormAuthId
    );
    if (isSettingAlreadyExist) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Setting name is already exist.");
      return;
    }
    this.handleAuthFields(data);
  };

  onFinishFailed = ({ errorFields }: ValidateErrorEntity<any>) => {
    switch (this.state.type) {
      case EProjectAuthType.genericAuth:
        this.onGenericSubmitFailed(errorFields);
        break;
      case EProjectAuthType.oAuth2:
        this.onOAuth2SubmitFailed(errorFields);
        break;
      default:
        break;
    }
  };

  onGenericSubmitFailed = (errorFields: ValidateErrorEntity<any>["errorFields"]) => {
    // ADD OTHER TAB FORM ITEMS IF NECESSARY
    const requiredResponseTokenFields: string[] = ["accessTokenName", "userNameKeyName"];
    for (let i = 0; i < errorFields.length; i++) {
      if (requiredResponseTokenFields.includes(errorFields[i].name[0] as string)) {
        this.handleTabChange("response");
        return;
      }
    }
  };

  onOAuth2SubmitFailed = (errorFields: ValidateErrorEntity<any>["errorFields"]) => {
    // ADD OTHER TAB FORM ITEMS IF NECESSARY
    const requiredResponseTokenFields: string[] = ["userNameKeyName"];
    for (let i = 0; i < errorFields.length; i++) {
      if (requiredResponseTokenFields.includes(errorFields[i].name[0] as string)) {
        this.handleTabChange("response");
        return;
      }
    }
  };

  handleTabChange = (activeKey) => {
    this.setState({ activeKey });
  };

  loadTokenParameters = (parameters) => {
    const tokenParameters: Array<IPropertiesObjectModel> = [];
    let index = 1;
    // headers
    let keys = Object.keys(parameters.headers);
    keys.forEach((element) => {
      if (parameters.headers[element] === "{password}")
        tokenParameters.push({
          id: index,
          name: "password",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      else if (parameters.headers[element] === "{username}")
        tokenParameters.push({
          id: index,
          name: "username",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      else {
        tokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      }
      index += 1;
    });
    keys = Object.keys(parameters.queryParameters);
    keys.forEach((element) => {
      if (parameters.queryParameters[element] === "{password}")
        tokenParameters.push({
          id: index,
          name: "password",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      else if (parameters.queryParameters[element] === "{username}")
        tokenParameters.push({
          id: index,
          name: "username",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      else {
        tokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      }
      index += 1;
    });
    keys = Object.keys(parameters.bodyParameters);
    keys.forEach((element) => {
      if (parameters.bodyParameters[element] === "{password}")
        tokenParameters.push({
          id: index,
          name: "password",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      else if (parameters.bodyParameters[element] === "{username}")
        tokenParameters.push({
          id: index,
          name: "username",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      else {
        tokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      }
      index += 1;
    });
    this.setState({ tokenParameters });
  };

  loadRefreshTokenParameters = (parameters) => {
    const refreshTokenParameters: Array<IPropertiesObjectModel> = [];
    let index = 1;
    let keys = Object.keys(parameters.headers);
    keys.forEach((element) => {
      if (parameters.headers[element] === "{token}")
        refreshTokenParameters.push({
          id: index,
          name: "token",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      else if (parameters.headers[element] === "{refreshtoken}")
        refreshTokenParameters.push({
          id: index,
          name: "refreshtoken",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      else {
        refreshTokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.headers[element],
          type: "header"
        });
      }
      index += 1;
    });
    keys = Object.keys(parameters.queryParameters);
    keys.forEach((element) => {
      if (parameters.queryParameters[element] === "{token}")
        refreshTokenParameters.push({
          id: index,
          name: "token",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      else if (parameters.queryParameters[element] === "{refreshtoken}")
        refreshTokenParameters.push({
          id: index,
          name: "refreshtoken",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      else {
        refreshTokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.queryParameters[element],
          type: "query"
        });
      }
      index += 1;
    });
    keys = Object.keys(parameters.bodyParameters);
    keys.forEach((element) => {
      if (parameters.bodyParameters[element] === "{token}")
        refreshTokenParameters.push({
          id: index,
          name: "token",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      else if (parameters.bodyParameters[element] === "{refreshtoken}")
        refreshTokenParameters.push({
          id: index,
          name: "refreshtoken",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      else {
        refreshTokenParameters.push({
          id: index,
          name: "custom",
          key: element,
          value: parameters.bodyParameters[element],
          type: "body"
        });
      }
      index += 1;
    });
    this.setState({ refreshTokenParameters });
  };

  handleAuthFields = (data) => {
    this.saveStatus.start();

    let genericAuth: IGenericAuthSetting | null = null;
    let oAuth2: IOAuth2Setting | null = null;
    let ldap: ILDAPSetting | null = null;
    if (this.state.type === 1) {
      const headers = {};
      const queryParameters = {};
      const bodyParameters = {};
      const refreshTokenHeaders = {};
      const refreshTokenQueryParameters = {};
      const refreshTokenBodyParameters = {};
      this.state.tokenParameters.forEach((x) => {
        if (x.type === "header") {
          headers[x.key?.trim()] = x.value?.trim();
        } else if (x.type === "query") {
          queryParameters[x.key?.trim()] = x.value?.trim();
        } else if (x.type === "body") {
          bodyParameters[x.key?.trim()] = x.value?.trim();
        }
      });

      this.state.refreshTokenParameters.forEach((x) => {
        if (x.type === "header") {
          refreshTokenHeaders[x.key?.trim()] = x.value?.trim();
        } else if (x.type === "query") {
          refreshTokenQueryParameters[x.key?.trim()] = x.value?.trim();
        } else if (x.type === "body") {
          refreshTokenBodyParameters[x.key?.trim()] = x.value?.trim();
        }
      });

      genericAuth = {
        id:
          this.props.editingFormAuthId && this.props.editingFormAuthId != ""
            ? this.props.editingFormAuthId
            : Guid.create().toString(),
        name: data.genericAuthName,
        type: EProjectAuthType.genericAuth,
        resultSchema: {
          accessTokenKeyName: data.accessTokenKeyName?.trim(),
          userNameKeyName: data.userNameKeyName?.trim(),
          firstNameKeyName: data.firstNameKeyName?.trim(),
          lastNameKeyName: data.lastNameKeyName?.trim(),
          refreshTokenKeyName: data.refreshTokenKeyName?.trim()
        } as AuthRestApiResultSchemaModel,
        tokenRequest: {
          url: data.tokenUrl?.trim(),
          tokenExpirationInMinutes: data.tokenExpirationInMinutes,
          contentType: data.tokenContentType?.trim(),
          headers,
          queryParameters,
          bodyParameters
        } as GenericRestApiResquestModel,
        refreshTokenRequest: {
          url: data.refreshTokenUrl?.trim(),
          contentType: data.refreshTokenContentType?.trim(),
          headers: refreshTokenHeaders,
          queryParameters: refreshTokenQueryParameters,
          bodyParameters: refreshTokenBodyParameters
        } as GenericRestApiResquestModel
      } as unknown as IGenericAuthSetting;
      this.handleSaveConfiguration(genericAuth, "genericAuthSettings");
      return;
    }
    if (this.state.type === 2) {
      oAuth2 = {
        id:
          this.props.editingFormAuthId && this.props.editingFormAuthId != ""
            ? this.props.editingFormAuthId
            : Guid.create().toString(),
        name: data.oAuth2Name,
        type: EProjectAuthType.oAuth2,
        resultSchema: {
          accessTokenKeyName: data.accessTokenKeyName?.trim(),
          userNameKeyName: data.userNameKeyName?.trim(),
          firstNameKeyName: data.firstNameKeyName?.trim(),
          lastNameKeyName: data.lastNameKeyName?.trim(),
          refreshTokenKeyName: data.refreshTokenKeyName?.trim()
        } as AuthRestApiResultSchemaModel,
        request: {
          accessTokenUrl: data.accessTokenUrl?.trim(),
          accessTokenExpirationInMinutes: data.accessTokenExpirationInMinutes,
          clientId: data.clientId?.trim(),
          clientSecret: data.clientId?.trim(),
          scope: data.scope?.trim(),
          clientAuthSendAsBasicAuthHeader: data.clientAuthSendAsBasicAuthHeader !== "false"
        } as IOAuth2RestApiResquestModel
      } as unknown as IOAuth2Setting;
      this.handleSaveConfiguration(oAuth2, "oAuth2Settings");
      return;
    }
    if (this.state.type === 3) {
      ldap = {
        id:
          this.props.editingFormAuthId && this.props.editingFormAuthId != ""
            ? this.props.editingFormAuthId
            : Guid.create().toString(),
        name: data.ldapName,
        type: EProjectAuthType.ldap,
        ipAddress: data.ipAddress?.trim(),
        userName: data.userName?.trim(),
        password: data.password?.trim(),
        port: data.port,
        dn: data.dn?.trim(),
        searchDn: data.searchDn?.trim(),
        searchTemplate: data.searchTemplate?.trim(),
        bindType: data.bindType,
        protocol: data.protocol,
        domainPrefixForUserName: data.domainPrefixForUserName?.trim(),
        userNameAttributeName: data.userNameAttributeName?.trim(),
        firstNameAttributeName: data.firstNameAttributeName?.trim(),
        lastNameAttributeName: data.lastNameAttributeName?.trim(),
        phoneNumberAttributeName: data.phoneNumberAttributeName?.trim()
      } as ILDAPSetting;
      this.handleSaveConfiguration(ldap, "ldapSettings");
      return;
    }

    KMainFunctions.displayErrorNotification("Please select a Authentication Provider type");
    this.saveStatus.reset();
    return;
  };

  handleSaveConfiguration(
    config: IGenericAuthSetting | IOAuth2Setting | ILDAPSetting,
    configKey: "genericAuthSettings" | "oAuth2Settings" | "ldapSettings"
  ) {
    this.saveStatus.start();
    if (this.props.editingFormAuthId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config,
        configKey,
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config,
        configKey,
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  }

  handleClickAddButton = (key: string, type: string): void => {
    const arrTokenParameters = [...this.state.tokenParameters];
    const arrRefreshTokenParameteres = [...this.state.refreshTokenParameters];
    if (type === "token") {
      if (key === "1")
        arrTokenParameters.push({
          id: arrTokenParameters[arrTokenParameters.length - 1].id + 1,
          name: "custom",
          key: "",
          value: "",
          type: "header"
        });
      else if (key === "2")
        arrTokenParameters.push({
          id: arrTokenParameters[arrTokenParameters.length - 1].id + 1,
          name: "username",
          key: "",
          value: "{username}",
          type: "header"
        });
      else
        arrTokenParameters.push({
          id: arrTokenParameters[arrTokenParameters.length - 1].id + 1,
          name: "password",
          key: "",
          value: "{password}",
          type: "header"
        });

      this.setState({ tokenParameters: arrTokenParameters });
    } else {
      if (key === "1")
        arrRefreshTokenParameteres.push({
          id: arrRefreshTokenParameteres[arrRefreshTokenParameteres.length - 1].id + 1,
          name: "custom",
          key: "",
          value: "",
          type: "header"
        });
      else if (key === "2")
        arrRefreshTokenParameteres.push({
          id: arrRefreshTokenParameteres[arrRefreshTokenParameteres.length - 1].id + 1,
          name: "token",
          key: "",
          value: "{token}",
          type: "header"
        });
      else
        arrRefreshTokenParameteres.push({
          id: arrRefreshTokenParameteres[arrRefreshTokenParameteres.length - 1].id + 1,
          name: "refreshtoken",
          key: "",
          value: "{refreshtoken}",
          type: "header"
        });
      this.setState({ refreshTokenParameters: arrRefreshTokenParameteres });
    }
  };

  onTokenParameterChange = (parameters) => {
    this.setState({ tokenParameters: parameters });
  };

  onRefreshTokenParameterChange = (parameters) => {
    this.setState({ refreshTokenParameters: parameters });
  };

  deleteFormAuthSetting = () => {
    const configurationKey = this.getCommonConfigKey();
    if (!this.props.editingFormAuthId || !configurationKey) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFormAuthId || "",
      configKey: configurationKey,

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Authentication Provider setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getCommonConfigKey() {
    switch (this.state.type) {
      case EProjectAuthType.genericAuth:
        return "genericAuthSettings";
      case EProjectAuthType.oAuth2:
        return "oAuth2Settings";
      case EProjectAuthType.ldap:
        return "ldapSettings";
      default:
        return;
    }
  }

  render() {
    let descriptionKey = "";
    let typeKey = "";
    switch (this.state.type) {
      case EProjectAuthType.genericAuth:
        descriptionKey = "authenticationprovidersettings_genericauth_authenticationprovidersettings_description";
        typeKey = "authenticationprovidersettings_genericauth_authenticationprovidersettings_type";
        break;
      case EProjectAuthType.oAuth2:
        descriptionKey = "authenticationprovidersettings_oauth2.0_authenticationprovidersettings_description";
        typeKey = "authenticationprovidersettings_oauth2.0_authenticationprovidersettings_type";
        break;
      case EProjectAuthType.ldap:
        descriptionKey = "authenticationprovidersettings_ldap_authenticationprovidersettings_description";
        typeKey = "authenticationprovidersettings_ldap_authenticationprovidersettings_type";
        break;
    }
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Authentication provider setting"
        secondHeader="Authentication provider setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFormAuthId ? this.deleteFormAuthSetting : undefined}
            deleteTestId="formAuthSettingDeleteButton"
            submitTestId="formAuthSettingCreateButton"
            submitLabel={this.props.editingFormAuthId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFormAuthenticationForm"
          />
        }
      >
        <Form
          id="configFormAuthenticationForm"
          name="formAuth"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody
            // Only once a provider type is picked: before that the copy describes nothing.
            description={this.state.type ? this.props.getSectionDescription(descriptionKey) : undefined}
          >
            <>
              <ConfigField label="Type" required tooltipContentKey={typeKey}>
                <Form.Item
                  name="type"
                  rules={[{ required: true, message: "Please select an Authentication Provider Type" }]}
                >
                  <KuikaSelectbox
                    size="md"
                    value={this.state.type || undefined}
                    onChange={(e) => {
                      this.setState({ type: e, activeKey: undefined });
                    }}
                    placeholder="Auth Type"
                    disabled={!!this.props.editingFormAuthId}
                    data-testid="authProviderTypeSelect"
                    options={[
                      { value: 1, label: "Generic Auth (REST)", testId: "authProviderTypeGenericOption" },
                      { value: 2, label: "oAuth 2.0", testId: "authProviderTypeOAuth2Option" },
                      { value: 3, label: "LDAP", testId: "authProviderTypeLdapOption" }
                    ]}
                  />
                </Form.Item>
              </ConfigField>
              {this.state.type === EProjectAuthType.genericAuth && (
                <AppConfigAuthGeneric
                  getSectionDescription={this.props.getSectionDescription}
                  activeKey={this.state.activeKey}
                  handleTabChange={this.handleTabChange}
                  editingId={this.props.editingFormAuthId}
                  handleClickAddButton={this.handleClickAddButton}
                  tokenParameters={this.state.tokenParameters}
                  refreshTokenParameters={this.state.refreshTokenParameters}
                  onTokenParameterChange={this.onTokenParameterChange}
                  onRefreshTokenParameterChange={this.onRefreshTokenParameterChange}
                  commonSettings={this.props.commonSettings}
                />
              )}
              {this.state.type === EProjectAuthType.oAuth2 && (
                <AppConfigAuthOAuth2
                  getSectionDescription={this.props.getSectionDescription}
                  editingId={this.props.editingFormAuthId}
                  activeKey={this.state.activeKey}
                  handleTabChange={this.handleTabChange}
                  commonSettings={this.props.commonSettings}
                />
              )}
              {this.state.type === EProjectAuthType.ldap && (
                <AppConfigAuthLdap
                  getSectionDescription={this.props.getSectionDescription}
                  editingId={this.props.editingFormAuthId}
                  commonSettings={this.props.commonSettings}
                />
              )}
            </>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
